import * as React from "react";
import Color from "color";
import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import MuiTabs from "@material-ui/core/Tabs";
import MuiTab from "@material-ui/core/Tab";
import Divider from "@material-ui/core/Divider";

import { translate } from "jazasoft";

import Table from "../../../components/Table";
import { getDistinctObjectValues, getDistinctValues, isEmpty, isFalse, toFixedValue } from "../../../utils/helpers";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";
import { useDeepMemo } from "../../../hooks";
import { SpreadingType } from "../../../utils/types";
import { colorLemom, colorLightGreen, colorSecondary } from "../../../utils/colors";
import { getMarkerList } from "../../cutplan/cutPlanHelper";

const colors = [
  Color(colorSecondary.light).lighten(0.4).hex(),
  Color(colorLightGreen.light).lighten(0.6).hex(),
  Color(colorLemom.light).lighten(0.4).hex()
];

const NON_SB_FIELDS = ["info", "totalPercent", "total"];

const useStyles = makeStyles(theme => ({
  mrTop: {
    marginTop: "1.5em"
  },
  table: {
    "& th, & td": {
      //   border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    // padding: 0,
    fontSize: 12,
    "&:last-child": {
      //   padding: 0,
      fontSize: 12
    }
  }
}));

const BundlingReport = props => {
  const classes = useStyles();
  const { order, flowInfoList, cutOrderList, bundleOrderList, layRowList, bundleList = [], translate } = props;

  const [tabIdx, setTabIdx] = React.useState(0);

  //** Prepare Size Breakup Columns**/
  const { columns, headerRows, sizeKeys } = useDeepMemo(() => {
    if (isEmpty(flowInfoList)) return { columns: [], headerRows: [] };

    const sizeBreakupList = flowInfoList.flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || []);
    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        leftBorder,
        element: ({ record, column }) => toFixedValue(record[column.dataKey], 0)
      });
    }

    const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let columns = [
      {
        dataKey: "bundleOrderNo",
        title: "Bundle Order No.",
        leftBorder: true,
        rowSpan: ({ record }) => (record.info === "Bundle Required Qty" ? 3 : 0)
      },
      { dataKey: "info", title: "Info", align: "left", leftBorder: true }
    ];

    columns = [
      ...columns,
      ...sizeList,
      {
        dataKey: "total",
        title: "Total",
        leftBorder: true,
        element: ({ record, column }) => toFixedValue(record[column.dataKey], 0)
      },
      { dataKey: "totalPercent", title: "Total (%)" }
    ];
    columns = columns.map(c => ({
      ...c,
      align: c.align || "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }

    if (tabIdx === 0) {
      columns = columns.filter(c => c.dataKey !== "bundleOrderNo");
    } else {
      columns = columns.filter(c => c.dataKey !== "totalPercent");
    }

    return { columns, headerRows, sizeKeys };
  }, [tabIdx, flowInfoList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(flowInfoList) || isEmpty(cutOrderList) || isEmpty(layRowList)) return [];
    const emptyQty = sizeKeys.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: 0 }), {});

    const productIdList = getDistinctValues(flowInfoList.map(fi => fi.productId).filter(e => !isEmpty(e)));
    const productId = productIdList[0];

    if (tabIdx === 0) {
      // Order & FPO Qty
      const sizeBreakupList = flowInfoList
        .filter(fi => fi.productId === productId)
        .flatMap(flowInfo => {
          const childList = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
          return isEmpty(childList) ? [flowInfo] : childList;
        })
        .flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER").map(sb => ({ ...sb, qty: sb.qty / (fi.ratio || 1) })) || []);
      const fpoSizeBreakupList = flowInfoList
        .filter(fi => fi.productId === productId)
        .flatMap(fi => fi.fpoSizeBreakupList.map(sb => ({ ...sb, qty: sb.qty / (fi.ratio || 1) })) || []);
      const orderQty = getDistinctObjectValues(sizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);
      const fpoQty = getDistinctObjectValues(fpoSizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);

      const sizeKeyList = sizeBreakupList.map(sb => sb.sizeKey);

      // Planned(Approved) Qty
      const plannedQtyMap = sizeKeyList.reduce((acc, sizeKey) => {
        const approvedQty = cutOrderList
          .map(cutOrder => {
            const approvedQtyMap = JSON.parse(cutOrder?.orderConfig?.approvedOrderCutQty || "{}");
            return Object.values(approvedQtyMap)
              .map(sbMap => sbMap[sizeKey] || 0)
              .reduce((acc, qty) => acc + qty, 0);
          })
          .reduce((acc, aQty) => acc + aQty, 0);
        return { ...acc, [sizeKey]: approvedQty };
      }, emptyQty);
      const plannedQty = Object.values(plannedQtyMap).reduce((acc, qty) => acc + qty, 0);

      // Cut Qty
      const markerList = cutOrderList.flatMap(e => getMarkerList(e));
      const finalLayRowList = layRowList
        .filter(row => isFalse(row.skipped))
        .map(row => ({ ...row, marker: markerList.find(m => row.markerId === m.id) }))
        .filter(row => !isEmpty(row.marker));
      const cutQtyMap = sizeKeyList.reduce((acc, sizeKey) => {
        const qty = finalLayRowList.reduce((acc, row) => {
          const sizeRatio = row.marker?.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
          const plies = SpreadingType.FACE_ONE_WAY !== row.marker?.spreadingType ? row.actualPly / 2 : row.actualPly;
          const qty = sizeRatio ? sizeRatio.ratio * plies : 0;
          return acc + qty;
        }, 0);
        return { ...acc, [sizeKey]: qty };
      }, emptyQty);
      const cutQty = Object.values(cutQtyMap).reduce((acc, qty) => acc + qty, 0);

      // Required Qty and Bundle Qty
      const boSizeBreakupList = getDistinctObjectValues(bundleOrderList)?.flatMap(bundleOrder => bundleOrder.boSizeBreakupList || []) || [];
      const bundlingQty = getDistinctObjectValues(boSizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);
      const bundleQtyMap = sizeKeyList.reduce((acc, sizeKey) => {
        const qty = bundleList.filter(b => b.sizeKey === sizeKey).reduce((acc, b) => acc + b.qty, 0);
        return { ...acc, [sizeKey]: qty };
      }, emptyQty);
      const bundleQty = Object.values(bundleQtyMap).reduce((acc, qty) => acc + qty, 0);

      let rowList = [
        {
          info: "Order Qty",
          total: orderQty,
          totalPercent: `100.0%`,
          ...getDistinctObjectValues(sizeBreakupList)?.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty)
        },
        {
          info: "FPO Qty",
          total: fpoQty,
          totalPercent: orderQty > 0 ? `${toFixedValue((fpoQty * 100) / orderQty, 1)}%` : 0,
          ...getDistinctObjectValues(fpoSizeBreakupList)?.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty)
        },
        {
          info: "Planned Qty",
          total: plannedQty,
          totalPercent: orderQty > 0 ? `${toFixedValue((plannedQty * 100) / orderQty, 1)}%` : 0,
          ...plannedQtyMap
        },
        {
          info: "Cut Qty",
          total: cutQty,
          totalPercent: orderQty > 0 ? `${toFixedValue((cutQty * 100) / orderQty, 1)}%` : 0,
          ...cutQtyMap
        },
        {
          info: "Bundle Required Qty",
          total: bundlingQty,
          totalPercent: orderQty > 0 ? `${toFixedValue((bundlingQty * 100) / orderQty, 1)}%` : 0,
          ...boSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty)
        },
        {
          info: "Bundle Completed Qty",
          total: bundleQty,
          totalPercent: orderQty > 0 ? `${toFixedValue((bundleQty * 100) / orderQty, 1)}%` : 0,
          ...bundleQtyMap
        }
        // { info: "Bal. Bundle Qty" }
      ];

      if (orderQty === fpoQty) {
        rowList = rowList.filter(e => e.info !== "FPO Qty");
      }

      return rowList;
    } else {
      const rowList = bundleOrderList
        .sort((a, b) => a.serialNo - b.serialNo)
        .flatMap(bundleOrder => {
          const boSizeBreakupList = bundleOrder.boSizeBreakupList || [];
          const bundlingQty = getDistinctObjectValues(boSizeBreakupList)?.reduce((acc, sb) => acc + sb.qty, 0);
          const bundlingQtyMap = getDistinctObjectValues(boSizeBreakupList)?.reduce(
            (acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }),
            emptyQty
          );

          const sizeKeyList = boSizeBreakupList.map(sb => sb.sizeKey);

          const currBundleList = bundleList.filter(b => b.bundleOrderId === bundleOrder.id);
          const bundleQtyMap = sizeKeyList.reduce((acc, sizeKey) => {
            const qty = currBundleList.filter(b => b.sizeKey === sizeKey).reduce((acc, b) => acc + b.qty, 0);
            return { ...acc, [sizeKey]: qty };
          }, emptyQty);
          const bundleQty = Object.values(bundleQtyMap).reduce((acc, qty) => acc + qty, 0);

          const balanceQtyMap = sizeKeyList.reduce((acc, sizeKey) => {
            return { ...acc, [sizeKey]: (bundlingQtyMap[sizeKey] || 0) - (bundleQtyMap[sizeKey] || 0) };
          }, emptyQty);
          const balanceQty = Object.values(balanceQtyMap).reduce((acc, qty) => acc + qty, 0);

          return [
            {
              bundleOrderNo: bundleOrder.bundleOrderNo,
              info: "Bundle Required Qty",
              total: bundlingQty,
              colorIdx: 0,
              ...bundlingQtyMap
            },
            {
              bundleOrderNo: bundleOrder.bundleOrderNo,
              info: "Bundle Completed Qty",
              total: bundleQty,
              colorIdx: 1,
              ...bundleQtyMap
            },
            {
              bundleOrderNo: bundleOrder.bundleOrderNo,
              info: "Balance Qty",
              total: balanceQty,
              colorIdx: 2,
              ...balanceQtyMap
            }
          ];
        });

      return rowList;
    }
  }, [tabIdx, flowInfoList, sizeKeys, cutOrderList, bundleOrderList, layRowList, bundleList]);

  return (
    <div>
      <Card className={classes.mrTop}>
        <MuiTabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
          <MuiTab label={translate("resources.bundling.title.bundling_summary", { _: "Bundling Summary" })} />
          <MuiTab label={translate("resources.bundling.title.bundling_detail", { _: "Bundling Detail" })} />
        </MuiTabs>
        <Divider />

        <div>
          {tabIdx === 0 && (
            <Table
              classes={{ table: classes.table }}
              columns={columns}
              headerRows={headerRows}
              rows={rows}
              headerCellStyle={column => ({
                borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                backgroundColor: "#C6D0DA"
              })}
              cellStyle={({ column, record }) => ({
                borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
              })}
            />
          )}
          {tabIdx === 1 && (
            <Table
              classes={{ table: classes.table }}
              columns={columns}
              headerRows={headerRows}
              rows={rows}
              headerCellStyle={column => ({
                borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                backgroundColor: "#C6D0DA"
              })}
              cellStyle={({ column, record }) => ({
                borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                backgroundColor: column.dataKey !== "bundleOrderNo" ? colors[record.colorIdx] : undefined
              })}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default translate(BundlingReport);
